import moment from 'moment';
import { PlanItemModel } from '@/libs/Api';
import { PlanItemsRepository } from '@/libs/tpm-analytics/data/plan-items.repository';
import { ShiftsRepository } from '@/libs/tpm-analytics/data/shifts.repository';
import { clampToRange } from '@/libs/tpm-analytics/core/time.utils';
import { calcPlanItemMaintenanceSplitKc } from '@/libs/tpm-analytics/core/financial.calculator';
import { createGrid, topNIndexes } from '@/libs/tpm-analytics/core/aggregation.utils';
import {
    FailuresByHourResult,
    TopDefectItem,
    TopMachineComponentItem,
    TopFaultTypeItem,
    FailureHeatmapResult,
    MaintenanceSummary,
    MtbfMttrByWorkplaceItem,
    SeverityDistributionItem,
    ShutdownHeatmapResult,
    LongestShutdownItem,
    AggregationDimension,
    AggregatedMaintenanceItem,
} from '@/libs/tpm-analytics/core/types';

/**
 * Načte poruchy (neplánované odstávky) přes PlanItems repozitář.
 * Důvod: znovupoužití server-side filtrů a vyloučení plánovaných odstávek.
 */
async function fetchMaintenanceFailures(
    fromIso: string,
    toIso: string,
    filters?: { locationIds?: string[]; workplaceIds?: string[]; projectIds?: string[]; shiftTypeIds?: string[]; shiftTeamTypeIds?: string[]; faultTypeIds?: string[] }
): Promise<PlanItemModel[]> {
    return PlanItemsRepository.fetchShutdownPlanItemsForRange([], fromIso, toIso, filters);
}

/**
 * Základní KPI pro údržbu: MTBF, MTTR, počet poruch, kritičnost, náklady na opravy.
 */
export async function getMaintenanceSummary(
    fromIso: string,
    toIso: string,
    filters?: { locationIds?: string[]; workplaceIds?: string[]; projectIds?: string[]; shiftTypeIds?: string[]; shiftTeamTypeIds?: string[]; faultTypeIds?: string[] }
): Promise<MaintenanceSummary> {
    const failures = await fetchMaintenanceFailures(fromIso, toIso, filters);
    const failuresCount = failures.length;
    const totalDowntimeMin = failures.reduce((s, p) => s + (Number(p.shutdownDuration ?? 0) || 0), 0);
    const perWp = await getMtbfMttrByWorkplace(fromIso, toIso, filters);
    const mtbfValues = perWp.map((x) => x.mtbfHours).filter((v): v is number => typeof v === 'number' && !isNaN(v as any));
    const mtbfHours = mtbfValues.length > 0 ? +(mtbfValues.reduce((a, b) => a + b, 0) / mtbfValues.length).toFixed(1) : null;
    const mttrHours = failuresCount > 0 ? +(totalDowntimeMin / failuresCount / 60).toFixed(1) : null;

    const criticalCount = failures.filter(
        (p) => (p as any).auditFaultType?.priority === 'P1' || (p as any).auditFaultType?.priority === 'P2'
    ).length;
    const repairCost = failures.reduce((sum, p: any) => sum + calcPlanItemMaintenanceSplitKc(p).repairKc, 0);
    // Zjistíme i počet MTBF vzorků tak, že znovu spočteme per-workplace a sečteme počty vzorků
    const mtbfSamplesCount = perWp.reduce(
        (s, x) => s + (isFinite((x as any)?.__mtbfSamplesCount) ? Number((x as any).__mtbfSamplesCount) : 0),
        0
    );
    return {
        mtbfHours,
        mttrHours,
        failuresCount,
        criticalCount,
        repairCost,
        meta: { mtbfSamplesCount, totalDowntimeMin },
    } as any;
}

/**
 * MTBF/MTTR po pracovištích – uptime/downtime odvozen z celkové délky období a sečtených odstávek.
 *
 * KLÍČOVÁ LOGIKA PRO MTBF:
 * - MTBF = čistý čas výroby / počet odstávek
 * - čistý čas výroby = čas směn - všechny odstávky (plánované i neplánované, pokud jde o odstávku stroje)
 * - překrývající se odstávky se spojí do jedné
 */
export async function getMtbfMttrByWorkplace(
    fromIso: string,
    toIso: string,
    filters?: { locationIds?: string[]; workplaceIds?: string[]; projectIds?: string[]; shiftTypeIds?: string[]; shiftTeamTypeIds?: string[]; faultTypeIds?: string[] }
): Promise<MtbfMttrByWorkplaceItem[]> {
    const [failures, shifts, plannedShutdowns] = await Promise.all([
        fetchMaintenanceFailures(fromIso, toIso, filters),
        ShiftsRepository.fetchShiftsForRange([], fromIso, toIso, filters),
        PlanItemsRepository.fetchPlannedShutdownPlanItemsForRange([], fromIso, toIso, filters),
    ]);

    type Interval = { fromMs: number; toMs: number };
    function clipInterval(startIso?: string | null, endIso?: string | null): Interval | null {
        const s0 = startIso ? Date.parse(startIso) : NaN;
        const e0 = endIso ? Date.parse(endIso) : NaN;
        if (!isFinite(s0) || !isFinite(e0)) return null;
        let s = s0;
        let e = e0;
        const f = Date.parse(fromIso);
        const t = Date.parse(toIso);
        if (isFinite(f) && s < f) s = f;
        if (isFinite(t) && e > t) e = t;
        if (e <= s) return null;
        return { fromMs: s, toMs: e };
    }

    /**
     * Sjednotí překrývající se intervaly pomocí sweep-line algoritmu.
     */
    function unionIntervals(intervals: Interval[]): Interval[] {
        if (!intervals || intervals.length === 0) return [];
        const events: { ts: number; k: 1 | -1 }[] = [];
        for (const iv of intervals) {
            events.push({ ts: iv.fromMs, k: 1 });
            events.push({ ts: iv.toMs, k: -1 });
        }
        events.sort((a, b) => (a.ts === b.ts ? a.k - b.k : a.ts - b.ts));
        const result: Interval[] = [];
        let active = 0;
        let segStart: number | null = null;
        for (const e of events) {
            const prevActive = active;
            active += e.k;
            if (prevActive === 0 && active > 0) segStart = e.ts;
            else if (prevActive > 0 && active === 0 && segStart != null && e.ts > segStart)
                result.push({ fromMs: segStart, toMs: e.ts });
        }
        return result;
    }

    /**
     * Odečte druhou sadu intervalů od první (A MINUS B).
     * Obě sady musí být již sjednocené.
     */
    function subtractIntervals(a: Interval[], b: Interval[]): Interval[] {
        if (!a || a.length === 0) return [];
        if (!b || b.length === 0) return a;

        // Pro každý interval v A odečteme všechny intervaly z B
        let result: Interval[] = [...a];
        for (const ivB of b) {
            const newResult: Interval[] = [];
            for (const ivA of result) {
                // Pokud se nepřekrývají, přidáme ivA beze změny
                if (ivA.toMs <= ivB.fromMs || ivA.fromMs >= ivB.toMs) {
                    newResult.push(ivA);
                    continue;
                }
                // Překryv - rozdělíme ivA na části před a po ivB
                if (ivA.fromMs < ivB.fromMs) {
                    newResult.push({ fromMs: ivA.fromMs, toMs: Math.min(ivA.toMs, ivB.fromMs) });
                }
                if (ivA.toMs > ivB.toMs) {
                    newResult.push({ fromMs: Math.max(ivA.fromMs, ivB.toMs), toMs: ivA.toMs });
                }
            }
            result = newResult;
        }
        return result;
    }

    /**
     * Vypočítá celkový čas intervalů v minutách.
     */
    function getTotalMinutes(intervals: Interval[]): number {
        return intervals.reduce((sum, iv) => sum + Math.floor((iv.toMs - iv.fromMs) / 60000), 0);
    }

    // Build shift unions per workplace
    const shiftByWp = new Map<string | null, Interval[]>();
    const workplaceNames = new Map<string | null, string>();
    for (const s of shifts as any[]) {
        const wid: string | null = s?.workplaceId ?? s?.workplace?.id ?? null;
        const iv = clipInterval(s?.fromDate, s?.toDate ?? s?.fromDate);
        if (!iv) continue;
        const arr = shiftByWp.get(wid) || [];
        arr.push(iv);
        shiftByWp.set(wid, arr);
        // Uložíme název pracoviště ze směny (pokud ještě nemáme)
        if (!workplaceNames.has(wid) && s?.workplace?.name) {
            workplaceNames.set(wid, s.workplace.name);
        }
    }
    const shiftUnionByWp = new Map<string | null, Interval[]>();
    for (const [wid, arr] of shiftByWp.entries()) {
        shiftUnionByWp.set(wid, unionIntervals(arr));
    }

    // Build planned shutdown unions per workplace
    const plannedByWp = new Map<string | null, Interval[]>();
    for (const p of plannedShutdowns as any[]) {
        const wid: string | null = (p as any).workplaceId ?? null;
        const iv = clipInterval((p as any).shutdownFromDate, (p as any).shutdownToDate ?? (p as any).shutdownFromDate);
        if (!iv) continue;
        const arr = plannedByWp.get(wid) || [];
        arr.push(iv);
        plannedByWp.set(wid, arr);
        // Uložíme název pracoviště i z plánovaných odstávek (pokud ještě nemáme)
        if (!workplaceNames.has(wid) && (p as any).workplace?.name) {
            workplaceNames.set(wid, (p as any).workplace.name);
        }
    }
    const plannedUnionByWp = new Map<string | null, Interval[]>();
    for (const [wid, arr] of plannedByWp.entries()) {
        plannedUnionByWp.set(wid, unionIntervals(arr));
    }

    // Build failures (unplanned) unions per workplace
    const failuresByWp = new Map<string | null, Interval[]>();
    for (const f of failures) {
        const wid: string | null = (f as any).workplaceId ?? null;
        const iv = clipInterval((f as any).shutdownFromDate, (f as any).shutdownToDate ?? (f as any).shutdownFromDate);
        if (!iv) continue;
        const arr = failuresByWp.get(wid) || [];
        arr.push(iv);
        failuresByWp.set(wid, arr);
        // Uložíme název pracoviště i z poruch (pokud ještě nemáme)
        if (!workplaceNames.has(wid) && (f as any).workplace?.name) {
            workplaceNames.set(wid, (f as any).workplace.name);
        }
    }
    const failuresUnionByWp = new Map<string | null, Interval[]>();
    for (const [wid, arr] of failuresByWp.entries()) {
        failuresUnionByWp.set(wid, unionIntervals(arr));
    }

    // NOVÁ LOGIKA: Spojíme všechny odstávky (plánované i neplánované) do jedné sady
    // a počítáme čistý čas výroby = čas směn - všechny odstávky
    const allShutdownsByWp = new Map<string | null, Interval[]>();
    for (const [wid, shiftUnion] of shiftUnionByWp.entries()) {
        const plannedUnion = plannedUnionByWp.get(wid) || [];
        const failuresUnion = failuresUnionByWp.get(wid) || [];

        // Spojíme plánované a neplánované odstávky do jedné sady
        // unionIntervals automaticky spojí překrývající se odstávky
        const allShutdowns = unionIntervals([...plannedUnion, ...failuresUnion]);
        allShutdownsByWp.set(wid, allShutdowns);
    }

    // Group failures per workplace and calculate MTBF
    type Acc = { name: string; count: number; downtimeMin: number };
    const map = new Map<string | null, Acc>();
    const byWp = new Map<string | null, any[]>();
    
    // Nejdříve seskupíme poruchy podle pracoviště
    for (const p of failures) {
        const key: string | null = (p as any).workplaceId ?? null;
        const name: string = (p as any).workplace?.name ?? '—';
        if (!map.has(key)) map.set(key, { name, count: 0, downtimeMin: 0 });
        const list = byWp.get(key) || [];
        list.push(p);
        byWp.set(key, list);
        const acc = map.get(key)!;
        acc.count += 1;
        acc.downtimeMin += Number((p as any).shutdownDuration ?? 0) || 0;
    }

    // Nyní spočítáme MTBF pro každé pracoviště
    const result: MtbfMttrByWorkplaceItem[] = Array.from(shiftUnionByWp.entries()).map(([workplaceId, shiftUnion]) => {
        const acc = map.get(workplaceId);
        // Název pracoviště získáme primárně ze směn, fallback na poruchy nebo '—'
        const name = workplaceNames.get(workplaceId) ?? acc?.name ?? '—';
        
        // Čas směn v minutách
        const totalShiftMin = getTotalMinutes(shiftUnion);
        
        // Všechny odstávky (spojené) v minutách
        const allShutdowns = allShutdownsByWp.get(workplaceId) || [];
        const totalShutdownMin = getTotalMinutes(allShutdowns);
        
        // Čistý čas výroby = čas směn - všechny odstávky
        const netProductionMin = totalShiftMin - totalShutdownMin;
        
        // Počet spojených odstávek (po spojení překrývajících se)
        const shutdownCount = allShutdowns.length;
        
        // MTBF = čistý čas výroby / počet odstávek
        const mtbfMin = shutdownCount > 0 && netProductionMin > 0 ? netProductionMin / shutdownCount : null;
        
        // MTTR zůstává stejné (průměrná délka neplánované odstávky)
        const failureCount = acc?.count ?? 0;
        const downtimeMin = acc?.downtimeMin ?? 0;
        
        const res = {
            workplaceId,
            workplaceName: name,
            mtbfHours: mtbfMin == null ? null : +((mtbfMin as number) / 60).toFixed(1),
            mttrHours: failureCount > 0 ? +(downtimeMin / failureCount / 60).toFixed(1) : null,
        } as MtbfMttrByWorkplaceItem;
        
        // Pro debugging - uložíme počet odstávek
        (res as any).__mtbfSamplesCount = shutdownCount;
        
        return res;
    });
    
    return result.sort((a, b) => (b.mtbfHours ?? -1) - (a.mtbfHours ?? -1));
}

/**
 * Rozložení poruch podle priority. Barvy přiřazovány dynamicky podle četnosti.
 */
export async function getSeverityDistribution(
    fromIso: string,
    toIso: string,
    filters?: { locationIds?: string[]; workplaceIds?: string[]; projectIds?: string[]; shiftTypeIds?: string[]; shiftTeamTypeIds?: string[]; faultTypeIds?: string[] }
): Promise<SeverityDistributionItem[]> {
    const failures = await fetchMaintenanceFailures(fromIso, toIso, filters);
    const counts = new Map<string, number>();

    // Sbíráme počty a první nalezenou barvu pro každou prioritu
    for (const p of failures) {
        const faultType = (p as any).auditFaultType;
        const priority = (faultType?.priority as string) || 'Other';
        counts.set(priority, (counts.get(priority) || 0) + 1);
    }

    // Paleta 50 barev - kontrastní barvy střídající se pro lepší rozlišení
    // Barvy jsou uspořádané tak, aby sousední měly maximální kontrast
    const colorPalette = [
        '#ef4444', // Červená
        '#3b82f6', // Modrá
        '#f97316', // Oranžová
        '#8b5cf6', // Fialová
        '#eab308', // Žlutá
        '#14b8a6', // Tyrkysová
        '#f43f5e', // Růžová
        '#06b6d4', // Cyan
        '#fb923c', // Světle oranžová
        '#6366f1', // Indigo
        '#84cc16', // Limetková
        '#ec4899', // Jasně růžová
        '#22c55e', // Zelená
        '#a855f7', // Fialová 2
        '#facc15', // Žlutá 2
        '#0ea5e9', // Světle modrá
        '#dc2626', // Tmavě červená
        '#7c3aed', // Tmavě fialová
        '#16a34a', // Tmavě zelená
        '#2563eb', // Královsky modrá
        '#f59e0b', // Amber
        '#d946ef', // Fuchsia
        '#65a30d', // Lime 2
        '#0891b2', // Cyan 2
        '#ea580c', // Oranžová 2
        '#9333ea', // Fialová 3
        '#10b981', // Emerald
        '#1d4ed8', // Modrá 2
        '#fbbf24', // Amber 2
        '#c026d3', // Fuchsia 2
        '#15803d', // Zelená 2
        '#7e22ce', // Fialová 4
        '#0284c7', // Sky blue
        '#dc2626', // Červená 2
        '#a21caf', // Fuchsia 3
        '#059669', // Emerald 2
        '#4f46e5', // Indigo 2
        '#d97706', // Amber 3
        '#db2777', // Pink
        '#166534', // Zelená 3
        '#5b21b6', // Fialová 5
        '#0e7490', // Cyan 3
        '#be123c', // Rose
        '#7dd3fc', // Sky
        '#fcd34d', // Žlutá 3
        '#a78bfa', // Fialová 6
        '#22d3ee', // Cyan 4
        '#fb7185', // Rose 2
        '#86efac', // Zelená 4
        '#c084fc', // Fialová 7
    ];

    // Seřadíme priority podle četnosti (sestupně)
    const sortedEntries = Array.from(counts.entries()).sort((a, b) => b[1] - a[1]);

    // Přiřadíme barvy podle pořadí četnosti
    const result: SeverityDistributionItem[] = sortedEntries.map(([label, count], index) => {
        // Pokud máme barvu ze serveru, použijeme ji
        // Jinak použijeme barvu z palety podle pořadí
        const paletteColor = colorPalette[index % colorPalette.length];

        return {
            label,
            count,
            color: paletteColor,
        };
    });

    // Debug log
    if ((window as any).__TPM_GQL_DEBUG) {
        // eslint-disable-next-line no-console
        console.log('[TPM][getSeverityDistribution] Result:', result);
    }

    return result;
}

/**
 * Heatmapa PRŮBĚHU neplánovaných odstávek (počty hodinově × dny v týdnu).
 * Použití: Identifikace časů s nejvyšší kumulovanou dobou odstávek.
 * 
 * Pro každou odstávku sčítáme všechny hodiny jejího průběhu v průniku s obdobím.
 * Tzn. pokud odstávka trvala od 10:00 do 13:00, přičteme +1 do hodin 10, 11, 12, 13.
 * 
 * Důležité: Moment.js automaticky konvertuje ISO datetime do lokálního času prohlížeče,
 * takže hodiny a dny odpovídají časovému pásmu uživatele.
 */
export async function getShutdownHeatmap(
    fromIso: string,
    toIso: string,
    filters?: { locationIds?: string[]; workplaceIds?: string[]; projectIds?: string[]; shiftTypeIds?: string[]; shiftTeamTypeIds?: string[]; faultTypeIds?: string[] }
): Promise<ShutdownHeatmapResult> {
    const items = await fetchMaintenanceFailures(fromIso, toIso, filters);
    const grid: number[][] = createGrid(7, 24, 0);
    const from = moment(fromIso).local();
    const to = moment(toIso).local();
    
    console.log(`[getShutdownHeatmap] Rozsah: od ${from.format('YYYY-MM-DD HH:mm:ss ddd')} do ${to.format('YYYY-MM-DD HH:mm:ss ddd')}, počet záznamů: ${items.length}`);
    console.log(`[getShutdownHeatmap] Timezone offset prohlížeče: ${moment().utcOffset()} minut (${moment().format('Z')})`);
    console.log(`[getShutdownHeatmap] fromIso: ${fromIso}, toIso: ${toIso}`);
    
    const debugItems: Array<{ from: string; to: string; fromLocal: string; toLocal: string }> = [];
    
    for (const p of items) {
        if (!p?.shutdownFromDate) continue;
        // Celý průběh odstávky (v lokálním čase prohlížeče)
        const startTime = moment(p.shutdownFromDate).local();
        const endTime = moment((p as any).shutdownToDate ?? p.shutdownFromDate).local();
        
        console.log(`[getShutdownHeatmap] Odstávka:`);
        console.log(`  original: ${p.shutdownFromDate} - ${(p as any).shutdownToDate || 'null'}`);
        console.log(`  parsed UTC: ${moment(p.shutdownFromDate).utc().format('YYYY-MM-DD HH:mm:ss')} - ${moment((p as any).shutdownToDate ?? p.shutdownFromDate).utc().format('YYYY-MM-DD HH:mm:ss')}`);
        console.log(`  local: ${startTime.format('YYYY-MM-DD HH:mm:ss ddd')} - ${endTime.format('YYYY-MM-DD HH:mm:ss ddd')}`);
        
        debugItems.push({
            from: p.shutdownFromDate,
            to: (p as any).shutdownToDate || p.shutdownFromDate,
            fromLocal: startTime.format('ddd DD.MM.YYYY HH:mm'),
            toLocal: endTime.format('ddd DD.MM.YYYY HH:mm'),
        });
        
        // Oříznutí na rozsah
        const start = clampToRange(startTime, from, to);
        const end = clampToRange(endTime, from, to);
        
        console.log(`  oříznutý: ${start.format('YYYY-MM-DD HH:mm:ss ddd')} - ${end.format('YYYY-MM-DD HH:mm:ss ddd')}`);
        
        // Pro každou hodinu průběhu přičteme 1 – bereme hodinu ze začátku každé hodiny v rozsahu
        const hours: string[] = [];
        for (let t = start.clone().startOf('hour'); t.isSameOrBefore(end); t.add(1, 'hour')) {
            const dIdx = t.isoWeekday() - 1; // 0 = Pondělí, 6 = Neděle
            const h = t.hour(); // 0-23
            hours.push(`${t.format('ddd')} ${h}:00`);
            if (dIdx >= 0 && dIdx < 7 && h >= 0 && h < 24) grid[dIdx][h] += 1;
        }
        console.log(`  hodiny: ${hours.join(', ')}`);
    }
    
    // Debug: Výpis gridu
    console.log('[getShutdownHeatmap] Finální grid:');
    const dayNames = ['Po', 'Út', 'St', 'Čt', 'Pá', 'So', 'Ne'];
    for (let d = 0; d < 7; d++) {
        const nonZero = grid[d].map((val, h) => val > 0 ? `${h}:${val}` : null).filter(x => x);
        if (nonZero.length > 0) {
            console.log(`  ${dayNames[d]}: ${nonZero.join(', ')}`);
        }
    }
    
    const daysInSpan = Math.max(1, to.diff(from, 'days') + 1);
    const totalCount = items.length;
    const avgPerDay = Math.round(totalCount / daysInSpan);
    
    // Kritické dny - pouze dny s nějakými daty
    const dayTotals = grid.map((row) => row.reduce((a, b) => a + b, 0));
    const criticalDayIndexes = dayTotals
        .map((v, i) => ({ i, v }))
        .filter((x) => x.v > 0) // Pouze dny s daty
        .sort((a, b) => b.v - a.v)
        .slice(0, 3)
        .map((x) => x.i);
    
    // Nejčastější doba - rozsah od první do poslední hodiny s daty
    const hourlyTotals: number[] = Array.from({ length: 24 }, (_, h) => grid.reduce((s, r) => s + r[h], 0));
    const totalHourlySum = hourlyTotals.reduce((a, b) => a + b, 0);
    
    let commonRangeFromHour = 0;
    let commonRangeToHour = 8;
    
    // Pouze pokud jsou nějaká data
    if (totalHourlySum > 0) {
        // Najdeme první a poslední hodinu s daty
        const firstHour = hourlyTotals.findIndex((v) => v > 0);
        const lastHour = hourlyTotals.length - 1 - [...hourlyTotals].reverse().findIndex((v) => v > 0);
        
        if (firstHour !== -1 && lastHour !== -1) {
            commonRangeFromHour = firstHour;
            commonRangeToHour = lastHour + 1; // +1 protože zobrazujeme "od-do" (např. 6:00 - 11:00 znamená včetně hodiny 10)
        }
    }
    
    return { grid, totalCount, avgPerDay, criticalDayIndexes, commonRangeFromHour, commonRangeToHour, debugItems };
}

/**
 * Počty poruch podle hodiny dne (určeno počáteční hodinou odstávky).
 */
export async function getFailuresByHour(fromIso: string, toIso: string): Promise<FailuresByHourResult> {
    const items = await fetchMaintenanceFailures(fromIso, toIso);
    const counts: number[] = Array.from({ length: 24 }, () => 0);
    for (const p of items) {
        const start = (p as any)?.shutdownFromDate || (p as any)?.time;
        if (!start) continue;
        const h = moment(start).local().hour();
        if (h >= 0 && h < 24) counts[h] += 1;
    }
    return { counts };
}

/**
 * Heatmapa ZAČÁTKŮ poruch (počty) – počítá pouze hodinu a den, kdy odstávka začala.
 * Použití: Identifikace časů, kdy nejčastěji vznikají poruchy.
 * 
 * Důležité: Moment.js automaticky konvertuje ISO datetime do lokálního času prohlížeče,
 * takže hodina a den odpovídají časovému pásmu uživatele.
 */
export async function getFailureHeatmap(
    fromIso: string,
    toIso: string,
    filters?: { locationIds?: string[]; workplaceIds?: string[]; projectIds?: string[]; shiftTypeIds?: string[]; shiftTeamTypeIds?: string[]; faultTypeIds?: string[] }
): Promise<FailureHeatmapResult> {
    const items = await fetchMaintenanceFailures(fromIso, toIso, filters);
    const grid: number[][] = createGrid(7, 24, 0);
    const from = moment(fromIso).local();
    const to = moment(toIso).local();
    
    console.log(`[getFailureHeatmap] Rozsah: od ${from.format('YYYY-MM-DD HH:mm:ss ddd')} do ${to.format('YYYY-MM-DD HH:mm:ss ddd')}, počet záznamů: ${items.length}`);
    
    const debugItems: Array<{ from: string; fromLocal: string }> = [];
    let totalFailures = 0;
    for (const p of items) {
        if (!p?.shutdownFromDate) continue;
        // Pouze začátek odstávky (v lokálním čase prohlížeče)
        const start = moment(p.shutdownFromDate).local();
        
        console.log(`[getFailureHeatmap] Začátek odstávky:`);
        console.log(`  original: ${p.shutdownFromDate}`);
        console.log(`  local: ${start.format('YYYY-MM-DD HH:mm:ss ddd')} (${start.format('ddd')} ${start.hour()}:00)`);
        
        // Pokud začátek není v našem rozsahu, přeskočíme
        if (start.isBefore(from) || start.isAfter(to)) {
            console.log(`  -> Mimo rozsah, přeskakuji`);
            continue;
        }
        
        debugItems.push({
            from: p.shutdownFromDate,
            fromLocal: start.format('ddd DD.MM.YYYY HH:mm'),
        });
        
        // Označíme pouze den a hodinu začátku
        const dIdx = start.isoWeekday() - 1; // 0 = Pondělí, 6 = Neděle
        const h = start.hour(); // 0-23
        
        if (dIdx >= 0 && dIdx < 7 && h >= 0 && h < 24) {
            console.log(`  -> Přidávám do grid[${dIdx}][${h}] (${start.format('ddd')} ${h}:00)`);
            grid[dIdx][h] += 1;
            totalFailures += 1;
        }
    }
    const daysInSpan = Math.max(1, to.diff(from, 'days') + 1);
    const avgPerDay = Math.round(totalFailures / daysInSpan);
    
    // Kritické dny - pouze dny s nějakými daty
    const dayTotals = grid.map((r) => r.reduce((a, b) => a + b, 0));
    const criticalDayIndexes = dayTotals
        .map((v, i) => ({ v, i }))
        .filter((x) => x.v > 0) // Pouze dny s daty
        .sort((a, b) => b.v - a.v)
        .slice(0, 3)
        .map((x) => x.i);
    
    // Top hodiny - pouze hodiny s nějakými daty
    const hourlyTotals: number[] = Array.from({ length: 24 }, (_, h) => grid.reduce((s, r) => s + r[h], 0));
    const topHoursData = hourlyTotals
        .map((v, i) => ({ v, i }))
        .filter((x) => x.v > 0) // Pouze hodiny s daty
        .sort((a, b) => b.v - a.v)
        .slice(0, 3)
        .map((x) => x.i)
        .sort((a, b) => a - b); // Seřadit podle času
    const topHours = topHoursData;
    
    return { grid, totalFailures, avgPerDay, criticalDayIndexes, topHours, debugItems };
}

/**
 * Heatmapa ZAČÁTKŮ plánovaných odstávek (počty) – počítá pouze hodinu a den, kdy odstávka začala.
 * Použití: Identifikace časů, kdy jsou nejčastěji plánovány odstávky.
 * 
 * Důležité: Moment.js automaticky konvertuje ISO datetime do lokálního času prohlížeče,
 * takže hodina a den odpovídají časovému pásmu uživatele.
 */
export async function getPlannedShutdownStartsHeatmap(
    fromIso: string,
    toIso: string,
    filters?: { locationIds?: string[]; workplaceIds?: string[]; projectIds?: string[]; shiftTypeIds?: string[]; shiftTeamTypeIds?: string[] }
): Promise<FailureHeatmapResult> {
    const items = await PlanItemsRepository.fetchPlannedShutdownPlanItemsForRange([], fromIso, toIso, filters);
    const grid: number[][] = createGrid(7, 24, 0);
    const from = moment(fromIso).local();
    const to = moment(toIso).local();
    
    console.log(`[getPlannedShutdownStartsHeatmap] Rozsah: od ${from.format('YYYY-MM-DD HH:mm:ss ddd')} do ${to.format('YYYY-MM-DD HH:mm:ss ddd')}, počet záznamů: ${items.length}`);
    
    const debugItems: Array<{ from: string; fromLocal: string }> = [];
    let totalFailures = 0;
    for (const p of items) {
        if (!(p as any)?.shutdownFromDate) continue;
        // Pouze začátek odstávky (v lokálním čase prohlížeče)
        const start = moment((p as any).shutdownFromDate).local();
        
        console.log(`[getPlannedShutdownStartsHeatmap] Začátek odstávky:`);
        console.log(`  original: ${(p as any).shutdownFromDate}`);
        console.log(`  local: ${start.format('YYYY-MM-DD HH:mm:ss ddd')} (${start.format('ddd')} ${start.hour()}:00)`);
        
        // Pokud začátek není v našem rozsahu, přeskočíme
        if (start.isBefore(from) || start.isAfter(to)) {
            console.log(`  -> Mimo rozsah, přeskakuji`);
            continue;
        }
        
        debugItems.push({
            from: (p as any).shutdownFromDate,
            fromLocal: start.format('ddd DD.MM.YYYY HH:mm'),
        });
        
        // Označíme pouze den a hodinu začátku
        const dIdx = start.isoWeekday() - 1; // 0 = Pondělí, 6 = Neděle
        const h = start.hour(); // 0-23
        
        if (dIdx >= 0 && dIdx < 7 && h >= 0 && h < 24) {
            console.log(`  -> Přidávám do grid[${dIdx}][${h}] (${start.format('ddd')} ${h}:00)`);
            grid[dIdx][h] += 1;
            totalFailures += 1;
        }
    }
    const daysInSpan = Math.max(1, to.diff(from, 'days') + 1);
    const avgPerDay = Math.round(totalFailures / daysInSpan);
    
    // Kritické dny - pouze dny s nějakými daty
    const dayTotals = grid.map((r) => r.reduce((a, b) => a + b, 0));
    const criticalDayIndexes = dayTotals
        .map((v, i) => ({ v, i }))
        .filter((x) => x.v > 0) // Pouze dny s daty
        .sort((a, b) => b.v - a.v)
        .slice(0, 3)
        .map((x) => x.i);
    
    // Top hodiny - pouze hodiny s nějakými daty
    const hourlyTotals: number[] = Array.from({ length: 24 }, (_, h) => grid.reduce((s, r) => s + r[h], 0));
    const topHoursData = hourlyTotals
        .map((v, i) => ({ v, i }))
        .filter((x) => x.v > 0) // Pouze hodiny s daty
        .sort((a, b) => b.v - a.v)
        .slice(0, 3)
        .map((x) => x.i)
        .sort((a, b) => a - b); // Seřadit podle času
    const topHours = topHoursData;
    
    return { grid, totalFailures, avgPerDay, criticalDayIndexes, topHours, debugItems };
}

/**
 * Heatmapa PRŮBĚHU plánovaných odstávek (počty hodinově × dny v týdnu).
 * Použití: Identifikace časů s nejvyšší kumulovanou dobou plánovaných odstávek.
 * 
 * Pro každou odstávku sčítáme všechny hodiny jejího průběhu v průniku s obdobím.
 * Tzn. pokud odstávka trvala od 10:00 do 13:00, přičteme +1 do hodin 10, 11, 12, 13.
 * 
 * Důležité: Moment.js automaticky konvertuje ISO datetime do lokálního času prohlížeče,
 * takže hodiny a dny odpovídají časovému pásmu uživatele.
 */
export async function getPlannedShutdownDurationHeatmap(
    fromIso: string,
    toIso: string,
    filters?: { locationIds?: string[]; workplaceIds?: string[]; projectIds?: string[]; shiftTypeIds?: string[]; shiftTeamTypeIds?: string[] }
): Promise<ShutdownHeatmapResult> {
    const items = await PlanItemsRepository.fetchPlannedShutdownPlanItemsForRange([], fromIso, toIso, filters);
    const grid: number[][] = createGrid(7, 24, 0);
    const from = moment(fromIso).local();
    const to = moment(toIso).local();
    
    console.log(`[getPlannedShutdownDurationHeatmap] Rozsah: od ${from.format('YYYY-MM-DD HH:mm:ss ddd')} do ${to.format('YYYY-MM-DD HH:mm:ss ddd')}, počet záznamů: ${items.length}`);
    
    const debugItems: Array<{ from: string; to: string; fromLocal: string; toLocal: string }> = [];
    
    for (const p of items) {
        if (!(p as any)?.shutdownFromDate) continue;
        // Celý průběh odstávky (v lokálním čase prohlížeče)
        const startTime = moment((p as any).shutdownFromDate).local();
        const endTime = moment((p as any).shutdownToDate ?? (p as any).shutdownFromDate).local();
        
        console.log(`[getPlannedShutdownDurationHeatmap] Odstávka:`);
        console.log(`  original: ${(p as any).shutdownFromDate} - ${(p as any).shutdownToDate || 'null'}`);
        console.log(`  local: ${startTime.format('YYYY-MM-DD HH:mm:ss ddd')} - ${endTime.format('YYYY-MM-DD HH:mm:ss ddd')}`);
        
        debugItems.push({
            from: (p as any).shutdownFromDate,
            to: (p as any).shutdownToDate || (p as any).shutdownFromDate,
            fromLocal: startTime.format('ddd DD.MM.YYYY HH:mm'),
            toLocal: endTime.format('ddd DD.MM.YYYY HH:mm'),
        });
        
        // Oříznutí na rozsah
        const start = clampToRange(startTime, from, to);
        const end = clampToRange(endTime, from, to);
        
        console.log(`  oříznutý: ${start.format('YYYY-MM-DD HH:mm:ss ddd')} - ${end.format('YYYY-MM-DD HH:mm:ss ddd')}`);
        
        // Pro každou hodinu průběhu přičteme 1 – bereme hodinu ze začátku každé hodiny v rozsahu
        const hours: string[] = [];
        for (let t = start.clone().startOf('hour'); t.isSameOrBefore(end); t.add(1, 'hour')) {
            const dIdx = t.isoWeekday() - 1; // 0 = Pondělí, 6 = Neděle
            const h = t.hour(); // 0-23
            hours.push(`${t.format('ddd')} ${h}:00`);
            if (dIdx >= 0 && dIdx < 7 && h >= 0 && h < 24) grid[dIdx][h] += 1;
        }
        console.log(`  hodiny: ${hours.join(', ')}`);
    }
    const daysInSpan = Math.max(1, to.diff(from, 'days') + 1);
    const totalCount = items.length;
    const avgPerDay = Math.round(totalCount / daysInSpan);
    
    // Kritické dny - pouze dny s nějakými daty
    const dayTotals = grid.map((row) => row.reduce((a, b) => a + b, 0));
    const criticalDayIndexes = dayTotals
        .map((v, i) => ({ i, v }))
        .filter((x) => x.v > 0) // Pouze dny s daty
        .sort((a, b) => b.v - a.v)
        .slice(0, 3)
        .map((x) => x.i);
    
    // Nejčastější doba - rozsah od první do poslední hodiny s daty
    const hourlyTotals: number[] = Array.from({ length: 24 }, (_, h) => grid.reduce((s, r) => s + r[h], 0));
    const totalHourlySum = hourlyTotals.reduce((a, b) => a + b, 0);
    
    let commonRangeFromHour = 0;
    let commonRangeToHour = 8;
    
    // Pouze pokud jsou nějaká data
    if (totalHourlySum > 0) {
        // Najdeme první a poslední hodinu s daty
        const firstHour = hourlyTotals.findIndex((v) => v > 0);
        const lastHour = hourlyTotals.length - 1 - [...hourlyTotals].reverse().findIndex((v) => v > 0);
        
        if (firstHour !== -1 && lastHour !== -1) {
            commonRangeFromHour = firstHour;
            commonRangeToHour = lastHour + 1; // +1 protože zobrazujeme "od-do" (např. 6:00 - 11:00 znamená včetně hodiny 10)
        }
    }
    
    return { grid, totalCount, avgPerDay, criticalDayIndexes, commonRangeFromHour, commonRangeToHour, debugItems };
}

export async function getTopDefects(
    fromIso: string,
    toIso: string,
    take = 10,
    filters?: { locationIds?: string[]; workplaceIds?: string[]; projectIds?: string[]; shiftTypeIds?: string[]; shiftTeamTypeIds?: string[] }
): Promise<TopDefectItem[]> {
    const current = await fetchMaintenanceFailures(fromIso, toIso, filters);
    const from = moment(fromIso);
    const to = moment(toIso);
    const spanMinutes = Math.max(0, to.diff(from, 'minutes'));
    const prevTo = from.clone();
    const prevFrom = from.clone().add(-spanMinutes, 'minutes');
    const previous = await fetchMaintenanceFailures(prevFrom.toISOString(), prevTo.toISOString(), filters);

    const agg = (list: PlanItemModel[]) => {
        const map = new Map<string, { code: string; name: string; count: number }>();
        for (const p of list as any[]) {
            const defectId = p?.defectId as string;
            if (!defectId) continue;
            
            const code = (p?.defect?.code as string) || '';
            const name = (p?.defect?.name as string) || 'Unknown';
            const key = defectId;
            const item = map.get(key) || { code, name, count: 0 };
            item.count += 1;
            map.set(key, item);
        }
        return map;
    };

    const cur = agg(current);
    const prev = agg(previous);
    const result: TopDefectItem[] = Array.from(cur.entries()).map(([_key, v]) => {
        const p = prev.get(_key);
        const changePct = p && p.count > 0 ? ((v.count - p.count) / p.count) * 100 : null;
        return {
            defectCode: v.code,
            defectName: v.name,
            count: v.count,
            changePct: changePct == null ? null : +changePct.toFixed(1),
            prevCount: p?.count ?? (null as any),
        } as TopDefectItem;
    });
    result.sort((a, b) => b.count - a.count);
    return result.slice(0, take);
}

export async function getTopMachineComponents(
    fromIso: string,
    toIso: string,
    take = 10,
    filters?: { locationIds?: string[]; workplaceIds?: string[]; projectIds?: string[]; shiftTypeIds?: string[]; shiftTeamTypeIds?: string[] }
): Promise<TopMachineComponentItem[]> {
    const current = await fetchMaintenanceFailures(fromIso, toIso, filters);
    const from = moment(fromIso);
    const to = moment(toIso);
    const spanMinutes = Math.max(0, to.diff(from, 'minutes'));
    const prevTo = from.clone();
    const prevFrom = from.clone().add(-spanMinutes, 'minutes');
    const previous = await fetchMaintenanceFailures(prevFrom.toISOString(), prevTo.toISOString(), filters);

    const agg = (list: PlanItemModel[]) => {
        const map = new Map<string, { code: string; name: string; count: number }>();
        for (const p of list as any[]) {
            const componentId = p?.machineComponentId as string;
            if (!componentId) continue;
            
            const code = (p?.machineComponent?.code as string) || '';
            const name = (p?.machineComponent?.name as string) || 'Unknown';
            const key = componentId;
            const item = map.get(key) || { code, name, count: 0 };
            item.count += 1;
            map.set(key, item);
        }
        return map;
    };

    const cur = agg(current);
    const prev = agg(previous);
    const result: TopMachineComponentItem[] = Array.from(cur.entries()).map(([_key, v]) => {
        const p = prev.get(_key);
        const changePct = p && p.count > 0 ? ((v.count - p.count) / p.count) * 100 : null;
        return {
            componentCode: v.code,
            componentName: v.name,
            count: v.count,
            changePct: changePct == null ? null : +changePct.toFixed(1),
            prevCount: p?.count ?? (null as any),
        } as TopMachineComponentItem;
    });
    result.sort((a, b) => b.count - a.count);
    return result.slice(0, take);
}

export async function getTopFaultTypes(
    fromIso: string,
    toIso: string,
    take = 10,
    filters?: { locationIds?: string[]; workplaceIds?: string[]; projectIds?: string[]; shiftTypeIds?: string[]; shiftTeamTypeIds?: string[]; faultTypeIds?: string[] }
): Promise<TopFaultTypeItem[]> {
    const current = await fetchMaintenanceFailures(fromIso, toIso, filters);
    const from = moment(fromIso);
    const to = moment(toIso);
    const spanMinutes = Math.max(0, to.diff(from, 'minutes'));
    const prevTo = from.clone();
    const prevFrom = from.clone().add(-spanMinutes, 'minutes');
    const previous = await fetchMaintenanceFailures(prevFrom.toISOString(), prevTo.toISOString(), filters);

    const agg = (list: PlanItemModel[]) => {
        const map = new Map<string, { count: number; cost: number }>();
        for (const p of list as any[]) {
            const name = (p?.auditFaultType?.name as string) || 'Unknown';
            const reactMin = Number(p?.reactionDuration ?? 0) || 0;
            const maintHourly = Number(p?.hourlyMaintenanceCosts ?? 0) || 0;
            const cost = (maintHourly / 60) * reactMin;
            const item = map.get(name) || { count: 0, cost: 0 };
            item.count += 1;
            item.cost += cost;
            map.set(name, item);
        }
        return map;
    };

    const cur = agg(current);
    const prev = agg(previous);
    const result: TopFaultTypeItem[] = Array.from(cur.entries()).map(([typeName, v]) => {
        const p = prev.get(typeName);
        const changePct = p && p.count > 0 ? ((v.count - p.count) / p.count) * 100 : null;
        return {
            typeName,
            count: v.count,
            changePct: changePct == null ? null : +changePct.toFixed(1),
            costKc: Math.round(v.cost),
            prevCount: p?.count ?? (null as any),
        } as TopFaultTypeItem;
    });
    result.sort((a, b) => b.count - a.count);
    return result.slice(0, take);
}

/**
 * Agreguje údržbová data podle vybraných dimenzí (lokace, stroj, komponenta, typ zastavení, typ poruchy).
 */
export async function getAggregatedMaintenanceData(
    fromIso: string,
    toIso: string,
    dimensions: AggregationDimension[],
    take = 10,
    filters?: { locationIds?: string[]; workplaceIds?: string[]; projectIds?: string[]; shiftTypeIds?: string[]; shiftTeamTypeIds?: string[] }
): Promise<AggregatedMaintenanceItem[]> {
    const current = await fetchMaintenanceFailures(fromIso, toIso, filters);
    const from = moment(fromIso);
    const to = moment(toIso);
    const spanMinutes = Math.max(0, to.diff(from, 'minutes'));
    const prevTo = from.clone();
    const prevFrom = from.clone().add(-spanMinutes, 'minutes');
    const previous = await fetchMaintenanceFailures(prevFrom.toISOString(), prevTo.toISOString(), filters);

    // Vytvoří klíč pro agregaci podle vybraných dimenzí
    const createKey = (p: any): string => {
        const parts: string[] = [];
        for (const dim of dimensions) {
            switch (dim) {
                case 'location':
                    parts.push(p?.workplace?.location?.id || '__no_value__');
                    break;
                case 'workplace':
                    parts.push(p?.workplaceId || '__no_value__');
                    break;
                case 'station':
                    parts.push(p?.stationId || '__no_value__');
                    break;
                case 'machineComponent':
                    parts.push(p?.machineComponentId || '__no_value__');
                    break;
                case 'shutdownType':
                    parts.push(p?.auditFaultTypeId || '__no_value__');
                    break;
                case 'defect':
                    parts.push(p?.defectId || '__no_value__');
                    break;
            }
        }
        return parts.join('|');
    };

    // Agregace dat
    const agg = (list: PlanItemModel[]) => {
        const map = new Map<string, AggregatedMaintenanceItem>();
        for (const p of list as PlanItemModel[]) {
            const key = createKey(p);

            let item = map.get(key);
            if (!item) {
                item = { count: 0, durationHours: 0, changePct: null } as AggregatedMaintenanceItem;
                
                // Naplnit data podle vybraných dimenzí
                for (const dim of dimensions) {
                    switch (dim) {
                        case 'location':
                            item.locationId = p?.workplace?.location?.id || '__no_value__';
                            item.locationName = p?.workplace?.location?.name || '--';
                            break;
                        case 'workplace':
                            item.workplaceId = p?.workplaceId || '__no_value__';
                            item.workplaceName = p?.workplace?.name || '--';
                            break;
                        case 'station':
                            item.stationId = p?.stationId || '__no_value__';
                            item.stationName = p?.station?.name || '--';
                            break;
                        case 'machineComponent':
                            item.machineComponentId = p?.machineComponentId || '__no_value__';
                            item.machineComponentCode = p?.machineComponent?.code || '--';
                            item.machineComponentName = p?.machineComponent?.name || '--';
                            break;
                        case 'shutdownType':
                            item.shutdownTypeId = p?.auditFaultTypeId || '__no_value__';
                            item.shutdownTypeName = p?.auditFaultType?.name || '--';
                            break;
                        case 'defect':
                            item.defectId = p?.defectId || '__no_value__';
                            item.defectCode = p?.defect?.code || '--';
                            item.defectName = p?.defect?.name || '--';
                            break;
                    }
                }
                map.set(key, item);
            }
            item.count += 1;
            
            // Přidat délku odstávky v hodinách
            if (p.shutdownFromDate && p.shutdownToDate) {
                const fromDate = new Date(p.shutdownFromDate);
                const toDate = new Date(p.shutdownToDate ?? new Date().toISOString());
                if (fromDate.getTime() > toDate.getTime()) {
                    continue;
                }
                const durationMs = toDate.getTime() - fromDate.getTime();
                const durationHours = durationMs / (1000 * 60 * 60);
                item.durationHours += durationHours;
            }
        }
        return map;
    };

    const cur = agg(current);
    const prev = agg(previous);
    
    const result: AggregatedMaintenanceItem[] = Array.from(cur.entries()).map(([key, v]) => {
        const p = prev.get(key);
        const changePct = p && p.count > 0 ? ((v.count - p.count) / p.count) * 100 : null;
        return {
            ...v,
            changePct: changePct == null ? null : +changePct.toFixed(1),
            prevCount: p?.count ?? (null as any),
            prevDurationHours: p?.durationHours ?? (null as any),
        };
    });
    
    // Výchozí řazení podle počtu
    result.sort((a, b) => b.count - a.count);
    return result.slice(0, take);
}

/**
 * Získá nejdelší neplánované odstávky.
 */
export async function getLongestShutdowns(
    fromIso: string,
    toIso: string,
    take = 10,
    filters?: { locationIds?: string[]; workplaceIds?: string[]; projectIds?: string[]; shiftTypeIds?: string[]; shiftTeamTypeIds?: string[] }
): Promise<LongestShutdownItem[]> {
    // Používáme speciální funkci, která načte i responses s problemDescription
    const items = await PlanItemsRepository.fetchShutdownPlanItemsWithResponsesForRange(
        [],
        fromIso,
        toIso,
        filters
    );

    console.log('[getLongestShutdowns] Celkový počet záznamů:', items.length);
    if (items.length > 0) {
        console.log('[getLongestShutdowns] Příklad prvního záznamu:', {
            workplace: items[0]?.workplace?.name,
            responses: items[0]?.responses,
            hasResponses: Array.isArray(items[0]?.responses),
            responsesCount: Array.isArray(items[0]?.responses) ? items[0].responses.length : 0,
        });
    }

    const result: LongestShutdownItem[] = items
        .map((item: any) => {
            const workplaceName = item?.workplace?.name || '—';
            const shutdownFrom = item?.shutdownFromDate || '';
            const shutdownTo = item?.shutdownToDate || shutdownFrom;
            const durationMinutes = Number(item?.shutdownDuration ?? 0) || 0;
            
            // Získáme problemDescription a immediateMeasures z responses
            let reason = '—';
            let immediateMeasures = '—';
            if (item?.responses && Array.isArray(item.responses) && item.responses.length > 0) {
                console.log('[getLongestShutdowns] Responses pro odstávku:', item.responses);
                // Vezmeme první response s problemDescription
                const responseWithProblem = item.responses.find((r: any) => r?.problemDescription);
                if (responseWithProblem?.problemDescription) {
                    reason = responseWithProblem.problemDescription;
                    console.log('[getLongestShutdowns] Nalezen důvod:', reason);
                }
                // Vezmeme první response s immediateMeasures
                const responseWithMeasures = item.responses.find((r: any) => r?.immediateMeasures);
                if (responseWithMeasures?.immediateMeasures) {
                    immediateMeasures = responseWithMeasures.immediateMeasures;
                    console.log('[getLongestShutdowns] Nalezena okamžitá opatření:', immediateMeasures);
                }
            }

            return {
                workplaceName,
                shutdownFrom,
                shutdownTo,
                durationMinutes,
                reason,
                immediateMeasures,
            };
        })
        .filter((x) => x.durationMinutes > 0) // Pouze odstávky s nenulovou délkou
        .sort((a, b) => b.durationMinutes - a.durationMinutes) // Seřadit podle délky (nejdelší nahoře)
        .slice(0, take);

    return result;
}
